import React, { useState, useEffect } from "react";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CForm,
  CFormLabel,
  CFormInput,
  CFormSelect,
  CButton,
  CRow,
  CCol,
} from "@coreui/react";
import { getUplines } from "src/services/Stores";
import { getSalesList, getProvincesBySMD, getCitiesBySMDAndProvince } from "src/services/SMD";

const StoreFilter = ({ open, onClose, onApply, initialFilters }) => {
  const [filters, setFilters] = useState(
    initialFilters || {
      name: "",
      type: "",
      smdId: "",
      status: "",
      daysSinceVisit: "",
      upline_name: "",
      provinceId: "",
      cityId: "",
    }
  );
  const [uplines, setUplines] = useState([]);
  const [loadingUplines, setLoadingUplines] = useState(false);
  const [smdOptions, setSmdOptions] = useState([]);
  const [loadingSMD, setLoadingSMD] = useState(false);
  const [smd, setSmd] = useState(0);
  const [provOptions, setProvOptions] = useState([]);
  const [prov, setProv] = useState(0);
  const [cityOptions, setCityOptions] = useState([]);

  // Fetch uplines for the dropdown
  useEffect(() => {
    const fetchUplines = async () => {
      setLoadingUplines(true);
      try {
        const response = await getUplines();
        const uplinesData = response?.data || response?.uplines || response || [];
        setUplines(uplinesData);
      } catch (error) {
        console.error("Error fetching uplines:", error);
        setUplines([]);
      } finally {
        setLoadingUplines(false);
      }
    };

    if (open) {
      fetchUplines();
    }
  }, [open]);

  // Fetch SMD options for the dropdown
  useEffect(() => {
    const fetchSMDOptions = async () => {
      setLoadingSMD(true);
      try {
        const response = await getSalesList();
        const smdData = response?.data || response || [];
        const options = smdData.map((smd) => ({
          value: smd.id,
          label: `${smd.name} - ${smd.id}`,
        }));
        setSmdOptions(options);
      } catch (error) {
        console.error("Error fetching SMD options:", error);
        setSmdOptions([]);
      } finally {
        setLoadingSMD(false);
      }
    };

    if (open) {
      fetchSMDOptions();
    }
  }, [open]);

  useEffect(() => {
    const fetchProvOptions = async (smd) => {
      try {
        const response = await getProvincesBySMD(smd);
        const provData = response?.data || response || [];
        const options = provData.map((prov) => ({
          value: prov.province_id,
          label: prov.province_name,
        }));
        setProvOptions(options);
      } catch (error) {
        console.error("Error fetching Prov options:", error);
        setProvOptions([]);
      }
    };

    if(smd !== "0") fetchProvOptions(smd)
  }, [smd])

  useEffect(() => {
    const fetchCityOptions = async (smd, prov) => {
      try {
        const response = await getCitiesBySMDAndProvince(smd, prov);
        const cityData = response?.data || response || [];
        const options = cityData.map((city) => ({
          value: city.city_id,
          label: city.city_name,
        }));
        setCityOptions(options);
      } catch (error) {
        console.error("Error fetching City options:", error);
        setCityOptions([]);
      }
    };

    if(prov !== "0") fetchCityOptions(smd, prov)
  }, [prov])

  const handleInputChange = (e) => {
    if (e.target.name == "provinceId") {
      setProv(e.target.value)
    } else if (e.target.name == "smdId") {
      setSmd(e.target.value)
    }
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onApply(filters);
  };

  const handleReset = () => {
    setFilters({
      name: "",
      type: "",
      smdId: "",
      status: "",
      daysSinceVisit: "",
      upline_name: "",
      provinceId: "",
      cityId: "",
    });
    onApply({}); // Apply empty filters to reset
  };

  return (
    <CModal visible={open} onClose={onClose} size="lg">
      <CModalHeader>
        <CModalTitle>Filter Toko</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CForm onSubmit={handleSubmit}>
          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel>Nama Toko</CFormLabel>
              <CFormInput
                type="text"
                name="name"
                value={filters.name}
                onChange={handleInputChange}
                placeholder="Cari berdasarkan nama"
              />
            </CCol>
            <CCol md={6}>
              <CFormLabel>Tipe Toko</CFormLabel>
              <CFormSelect name="type" value={filters.type} onChange={handleInputChange}>
                <option value="">Semua Tipe</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
              </CFormSelect>
            </CCol>
          </CRow>

          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel>SMD</CFormLabel>
              <CFormSelect 
                name="smdId" 
                value={filters.smdId} 
                onChange={handleInputChange}
                disabled={loadingSMD}
              >
                <option value="">Semua SMD</option>
                {smdOptions.map((smd) => (
                  <option key={smd.value} value={smd.value}>
                    {smd.label}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol md={6}>
              <CFormLabel>Upline</CFormLabel>
              <CFormSelect 
                name="upline_name" 
                value={filters.upline_name} 
                onChange={handleInputChange}
                disabled={loadingUplines}
              >
                <option value="">Semua Upline</option>
                <option value="no_upline">No Upline</option>
                {uplines.map((upline) => (
                  <option key={upline.id} value={upline.id}>
                    {upline.name}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
          </CRow>

          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel>Provinsi</CFormLabel>
              <CFormSelect 
                name="provinceId" 
                value={filters.provinceId} 
                onChange={handleInputChange}
                // disabled={smd == "0"}
              >
                <option value={null}>Semua Provinsi</option>
                {provOptions.map((prov) => (
                  <option key={prov.value} value={prov.value}>
                    {prov.label}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol md={6}>
              <CFormLabel>Kota</CFormLabel>
              <CFormSelect 
                name="cityId" 
                value={filters.cityId} 
                onChange={handleInputChange}
                disabled={prov == "0"}
              >
                <option value={null}>Semua Kota</option>
                {/* <option value="no_upline">No Upline</option> */}
                {cityOptions.map((city) => (
                  <option key={city.value} value={city.value}>
                    {city.label}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
          </CRow>

          <CRow className="mb-3">
            <CCol md={6}>
              <CFormLabel>Hari Sejak Kunjungan Terakhir</CFormLabel>
              <CFormSelect name="daysSinceVisit" value={filters.daysSinceVisit} onChange={handleInputChange}>
                <option value="">Semua</option>
                <option value="7">&lt; 7 hari</option>
                <option value="14">7-14 hari</option>
                <option value="30">15-30 hari</option>
                <option value="31">&gt; 30 hari</option>
              </CFormSelect>
            </CCol>
          </CRow>
        </CForm>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={handleReset}>
          Reset
        </CButton>
        <CButton color="primary" onClick={handleSubmit}>
          Terapkan Filter
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default StoreFilter;
